import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { images } from '../../data/images';
import { getCurrentCreators, getUpcomingCreators } from '../../utils/catalog';
import { formatDate } from '../../utils/exhibition';
import { easeOut } from '../../utils/motion';
import { btnPrimary, btnSecondary } from '../ui/buttonStyles';
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '10%']);
  const current = getCurrentCreators();
  const next = getUpcomingCreators()[0];
  const lines = ['Vingt créateurs.', 'Une saison.', 'Puis tout change.'];
  return <section ref={ref} className="mx-auto max-w-[1440px] px-5 pb-12 pt-6 md:px-8 md:pb-20 md:pt-10" data-id="element-307" data-mp="1q6d965yk6gdl">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-12" data-id="element-308" data-mp="1mvjdxzw8q3u9">
        <div className="order-2 lg:order-1 lg:col-span-5 lg:pb-6" data-id="element-309" data-mp="1rvyroskfpdcr">
          <h1 className="font-display text-[52px] leading-[0.95] tracking-tight md:text-7xl xl:text-[92px]" data-id="element-310" data-mp="1rt2sz20rikn9">
            {lines.map((line, i) => <motion.span key={line} className={`block ${i === 1 ? 'italic text-clay' : ''}`} initial={reduce ? false : {
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: easeOut,
            delay: 0.08 + i * 0.07
          }} data-id="element-311" data-mp="1hyaw60f081tj" data-mpi={i}>
                {line}
              </motion.span>)}
          </h1>
          <motion.p className="mt-6 max-w-md text-base leading-relaxed text-muted md:text-lg" initial={reduce ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          delay: 0.32
        }} data-id="element-312" data-mp="20uiyavcpzwkm">
            Chaque créateur expose ses pièces environ trois mois, puis cède sa place. Ce que vous voyez aujourd’hui ne
            sera peut-être plus là demain — c’est tout l’intérêt.
          </motion.p>
          <div className="mt-8 flex flex-wrap gap-3" data-id="element-313" data-mp="2u6nne9uro954">
            <Link to="/boutique" className={btnPrimary} data-id="element-314" data-mp="2u9jna3q6d7w7">
              Découvrir la saison <ArrowRightIcon className="h-4 w-4" data-id="element-315" data-mp="2p8cga6vuwcql" />
            </Link>
            <Link to="/createurs" className={btnSecondary} data-id="element-316" data-mp="2kakkh1s3c6h4">
              Rencontrer les créateurs
            </Link>
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-line pt-5" data-id="element-317" data-mp="2fgpujglw6v9l">
            <div data-id="element-318" data-mp="2ak94uhz1o8ue">
              <dt className="text-xs text-muted" data-id="element-319" data-mp="2ahn16tf9ydhg">En vitrine aujourd’hui</dt>
              <dd className="mt-1 font-display text-3xl" data-id="element-320" data-mp="26jp9njy17faw">{current.length} créateurs</dd>
            </div>
            {next && <div data-id="element-321" data-mp="21kvwjsmnpqb9">
                <dt className="text-xs text-muted" data-id="element-322" data-mp="1wk87hchznjjo">Prochaine arrivée</dt>
                <dd className="mt-1 font-display text-3xl" data-id="element-323" data-mp="1wm1c6m91jtp1">{formatDate(next.exhibitionStart)}</dd>
                <dd className="text-xs text-muted" data-id="element-324" data-mp="c40eq51k5on8">
                  <Link to={`/createurs/${next.id}`} className="hover:text-clay" data-id="element-325" data-mp="gzxt45t9gkxd">
                    {next.name}, {next.craft.toLowerCase()}
                  </Link>
                </dd>
              </div>}
          </dl>
        </div>
        <motion.div className="order-1 lg:order-2 lg:col-span-7" initial={reduce ? false : {
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: easeOut
      }} data-id="element-326" data-mp="pzkrsjulvcmq">
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-shell lg:aspect-auto lg:h-[min(78vh,760px)]" data-id="element-327" data-mp="2b1m8c2xxdyc">
            <motion.img src={images.hero} alt="L’intérieur de la boutique : céramiques, bijoux, chapeaux et textiles des créateurs de la saison" style={{
            y,
            scale: 1.1
          }} className="h-full w-full object-cover" data-id="element-328" data-mp="74e1hvlqc8ef" />
          </div>
        </motion.div>
      </div>
    </section>;
}